import React, { useEffect, useCallback, useMemo } from 'react';
import { Center } from '@fiverr-private/layout_components';
import { Typography } from '@fiverr-private/typography';
import { NicheGrid, NOT_SURE_NICHE } from '@fiverr-private/store_builder_ui';
import { useImpressionContainer, useImpressionItem } from '@fiverr-private/impressionable';
import { stepMetaMap, onImpression } from '@fiverr-private/store_builder_bi_events';
import type { ValidationStatus } from '@fiverr-private/store_builder_types';
import { AppI18n } from '../../i18n';
import { useInitialProps } from '../../hooks/useInitialProps';
import { useAutoSave } from '../../hooks/useAutoSave';
import { useStoreData } from '../../hooks/useStoreData';
import { useStepValidation } from '../../hooks/use-step-validation/useStepValidation';
import { useWizardNavigation } from '../../hooks/use-wizard-navigation/useWizardNavigation';

export const ChooseNiche: React.FC = () => {
    const { nicheData } = useInitialProps();
    const { storeData } = useStoreData();
    const { autoSave } = useAutoSave();
    const { currentPath } = useWizardNavigation();
    const { registerStepValidation, unregisterStepValidation, registerValidationOnNext, unregisterValidationOnNext } =
        useStepValidation();
    const [pageImpressionRef, pageImpressionId] = useImpressionContainer();

    useImpressionItem({
        selfRef: pageImpressionRef,
        selfId: pageImpressionId,
        sendImpressionToBQ: false,
        enrichment: {
            eventType: stepMetaMap[currentPath].impressionName,
            componentName: stepMetaMap[currentPath].name,
        },
        intersectionThreshold: 0,
        onImpression,
    });

    const validateNiche = useCallback(
        (): ValidationStatus => (storeData?.niche?.id ? 'valid' : 'invalid'),
        [storeData?.niche?.id]
    );

    useEffect(() => {
        registerStepValidation('choose-niche', validateNiche);
        registerValidationOnNext('choose-niche', () => Promise.resolve(validateNiche()));

        return () => {
            unregisterStepValidation('choose-niche');
            unregisterValidationOnNext('choose-niche');
        };
    }, [
        validateNiche,
        registerStepValidation,
        registerValidationOnNext,
        unregisterStepValidation,
        unregisterValidationOnNext,
    ]);

    const displayItems = useMemo(() => [...nicheData, NOT_SURE_NICHE], [nicheData]);

    const nicheDataWithHandlers = displayItems?.map((niche) => {
        const isSameId = storeData?.niche?.id === niche.id;
        const isDisplayNameMatch = storeData?.niche?.displayName === niche.displayName;
        const isSelected = isSameId && isDisplayNameMatch;

        return {
            ...niche,
            isSelected,
            onClick: () => autoSave({ niche, banners: [] }),
        };
    });

    return (
        <Center direction="row" ref={pageImpressionRef}>
            <NicheGrid
                items={nicheDataWithHandlers}
                as="button"
                HeaderComponent={
                    <Typography as="h4" fontWeight="bold" color="grey_1200" marginLeft="4" marginBottom="8">
                        <AppI18n k="steps.choose_niche.title" />
                    </Typography>
                }
            />
        </Center>
    );
};

ChooseNiche.displayName = 'ChooseNiche';
